import { Injectable } from '@angular/core';

import { defer, delay, type Observable, of, throwError } from 'rxjs';

import type {
  EventResendWebhook,
  FailedEvent,
  FailedEventsQuery,
  ResendRequest,
  ResendResult,
} from '../../interfaces';
import type { EventResendGateway } from './event-resend-gateway';

/** Client id that makes every mock call fail, to exercise error states. */
export const MOCK_ERROR_CLIENT_ID = 'mock-error';
/** Client id that makes the mock return no webhooks and no events. */
export const MOCK_EMPTY_CLIENT_ID = 'mock-empty';
/** Reason given when a requested event is no longer in the failed list. */
export const STALE_EVENT_REASON = 'El evento ya no está en la lista de fallidos';

const LATENCY_MS = 400;
const EVENT_COUNT = 40;
const HOUR_MS = 3_600_000;
const EVENT_TYPES = [
  'order.created',
  'order.picked',
  'order.cancelled',
  'order.delivered',
] as const;
const REJECTION_REASONS = [
  'El destino respondió 503',
  'Tiempo de espera agotado',
  'El destino respondió 400',
] as const;

const MOCK_WEBHOOKS: EventResendWebhook[] = [
  { id: 'wh-1', name: 'Órdenes', url: 'https://hooks.example.com/orders', active: true },
  { id: 'wh-2', name: 'Inventario', url: 'https://hooks.example.com/stock', active: true },
  { id: 'wh-3', name: 'Legado', url: 'https://legacy.example.com/events', active: false },
];

/** Builds the fixed failed-event list: `evt-001` is the newest. */
function buildEvents(): FailedEvent[] {
  const now = Date.now();
  return Array.from({ length: EVENT_COUNT }, (_, index) => ({
    id: `evt-${String(index + 1).padStart(3, '0')}`,
    type: EVENT_TYPES[index % EVENT_TYPES.length],
    webhookId: MOCK_WEBHOOKS[index % MOCK_WEBHOOKS.length].id,
    failureReason: REJECTION_REASONS[index % REJECTION_REASONS.length],
    failedAt: new Date(now - (index + 1) * HOUR_MS).toISOString(),
  }));
}

/**
 * In-memory stand-in for the event resend backend: fixed data, a short latency and failure modes
 * driven by well-known client ids. Resent events leave the list until the page is reloaded.
 * The special client ids are honored only here, never by a real gateway.
 */
@Injectable({ providedIn: 'root' })
export class EventResendMockService implements EventResendGateway {
  readonly isMock = true;

  private readonly events = buildEvents();
  private readonly resent = new Set<string>();

  /** Returns the fixed mock webhooks; none for {@link MOCK_EMPTY_CLIENT_ID}. */
  listWebhooks(query: FailedEventsQuery): Observable<EventResendWebhook[]> {
    return this.respond(query.clientId, () => MOCK_WEBHOOKS.map((webhook) => ({ ...webhook })), []);
  }

  /** Returns the fixed events minus the ones already resent; none for {@link MOCK_EMPTY_CLIENT_ID}. */
  listFailedEvents(query: FailedEventsQuery): Observable<FailedEvent[]> {
    return this.respond(
      query.clientId,
      () => this.events.filter((event) => !this.resent.has(event.id)),
      [],
    );
  }

  /**
   * Simulates the outcome of each event: ids whose number is divisible by 7 are rejected, and an
   * unknown or already resent id is rejected with {@link STALE_EVENT_REASON}.
   */
  resendEvents(request: ResendRequest): Observable<ResendResult> {
    return this.respond(
      request.clientId,
      () => ({
        outcomes: request.eventIds.map((eventId) => {
          const known = this.events.some((event) => event.id === eventId);
          if (!known || this.resent.has(eventId)) {
            return { eventId, accepted: false, reason: STALE_EVENT_REASON };
          }
          if (Number(eventId.slice(-3)) % 7 === 0) {
            return { eventId, accepted: false, reason: REJECTION_REASONS[0] };
          }
          this.resent.add(eventId);
          return { eventId, accepted: true };
        }),
      }),
      { outcomes: [] },
    );
  }

  private respond<T>(clientId: string, produce: () => T, emptyValue: T): Observable<T> {
    if (clientId === MOCK_ERROR_CLIENT_ID) {
      return throwError(() => new Error('Mock failure')).pipe(delay(LATENCY_MS));
    }
    if (clientId === MOCK_EMPTY_CLIENT_ID) {
      return of(emptyValue).pipe(delay(LATENCY_MS));
    }
    return defer(() => of(produce())).pipe(delay(LATENCY_MS));
  }
}
